import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Helmet } from 'react-helmet';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

const Register = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [provinces, setProvinces] = useState([]);
  const { register: registerUser } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    watch
  } = useForm();

  const password = watch('password');

  // Load provinces on component mount
  useEffect(() => {
    const loadProvinces = async () => {
      try {
        const response = await api.reference.getProvinces();
        const uniqueProvinces = Array.from(
          new Map((response.data.data || []).map((province) => [province.description.trim().toLowerCase(), province])).values()
        );
        setProvinces(uniqueProvinces);
      } catch (error) {
        console.error('Failed to load provinces:', error);
        console.error('Error details:', error.response?.data);
      }
    };

    loadProvinces();
  }, []);

  const onSubmit = async (data) => {
    console.log('🚀 FRONTEND REGISTRATION: Form data being submitted:', JSON.stringify(data, null, 2));
    setIsLoading(true);

    try {
      const result = await registerUser(data);
      
      if (!result.success) {
        if (result.details && Array.isArray(result.details)) {
          // Handle validation errors
          result.details.forEach(error => {
            setError(error.field, {
              type: 'server',
              message: error.message
            });
          });
        } else {
          setError('root', {
            type: 'server',
            message: result.error
          });
        }
      }
    } catch (error) {
      setError('root', {
        type: 'server',
        message: 'An unexpected error occurred'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <Helmet>
        <title>Sign Up - FBRSTAPP</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-between items-center gap-3 py-4">
            <Link to="/" className="flex items-center">
              <img 
                src="/fbrlogo1.svg" 
                alt="FBR Logo" 
                className="h-10 w-10 mr-3"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <div className="min-w-0">
                <h1 className="text-2xl font-bold text-blue-600">FBRSTAPP</h1>
                <p className="hidden sm:block text-sm text-gray-600">Cloud-Based FBR Digital Invoicing</p>
              </div>
            </Link>
            <div className="flex items-center gap-2 text-sm sm:text-base">
              <Link
                to="/"
                className="px-2 sm:px-4 py-2 text-gray-600 hover:text-gray-700 font-medium"
              >
                ← Back to Home
              </Link>
              <Link
                to="/login"
                className="px-3 sm:px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900">
                Get Started Today
              </h2>
              <p className="mt-2 text-gray-600">
                Create your FBRSTAPP account and start submitting invoices
              </p>
            </div>
        
            <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
              {errors.root && (
                <div className="rounded-md bg-red-50 p-4">
                  <div className="text-sm text-red-700">
                    {errors.root.message}
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label htmlFor="ntn_cnic" className="label">
                    NTN/CNIC
                  </label>
                  <input
                    id="ntn_cnic"
                    type="text"
                    className={`input-field ${errors.ntn_cnic ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                    placeholder="Enter your NTN or CNIC"
                    {...register('ntn_cnic', {
                      required: 'NTN/CNIC is required',
                      minLength: {
                        value: 5,
                        message: 'NTN/CNIC must be at least 5 characters'
                      },
                      maxLength: {
                        value: 20,
                        message: 'NTN/CNIC must not exceed 20 characters'
                      }
                    })}
                  />
                  {errors.ntn_cnic && (
                    <p className="error-text">{errors.ntn_cnic.message}</p>
                  )}
                </div>

            <div>
              <label htmlFor="business_name" className="label">
                Business Name
              </label>
              <input
                id="business_name"
                type="text"
                className={`input-field ${errors.business_name ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                placeholder="Enter your business name"
                {...register('business_name', {
                  required: 'Business name is required',
                  minLength: {
                    value: 2,
                    message: 'Business name must be at least 2 characters'
                  },
                  maxLength: {
                    value: 255,
                    message: 'Business name must not exceed 255 characters'
                  }
                })}
              />
              {errors.business_name && (
                <p className="error-text">{errors.business_name.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="province" className="label">
                Province
              </label>
              <select
                id="province"
                className={`input-field ${errors.province ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                {...register('province', {
                  required: 'Province is required'
                })}
              >
                <option value="">Select a province</option>
                {provinces.map((province) => (
                  <option key={province.code} value={province.description}>
                    {province.description}
                  </option>
                ))}
              </select>
              {errors.province && (
                <p className="error-text">{errors.province.message}</p>
              )}
              {provinces.length === 0 && (
                <p className="text-sm text-gray-500 mt-1">
                  Loading provinces... If this persists, check the console for errors.
                </p>
              )}
            </div>

            <div>
              <label htmlFor="address" className="label">
                Address
              </label>
              <textarea
                id="address"
                rows={3}
                className={`input-field ${errors.address ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                placeholder="Enter your complete address"
                {...register('address', {
                  required: 'Address is required',
                  minLength: {
                    value: 10,
                    message: 'Address must be at least 10 characters'
                  },
                  maxLength: {
                    value: 500,
                    message: 'Address must not exceed 500 characters'
                  }
                })}
              />
              {errors.address && (
                <p className="error-text">{errors.address.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="mobile_number" className="label">
                Mobile Number
              </label>
              <input
                id="mobile_number"
                type="tel"
                className={`input-field ${errors.mobile_number ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                placeholder="Enter your mobile number (e.g., 03001234567)"
                {...register('mobile_number', {
                  required: 'Mobile number is required',
                  pattern: {
                    value: /^(\+92|0)?[0-9]{10,11}$/,
                    message: 'Please enter a valid Pakistani mobile number'
                  }
                })}
              />
              {errors.mobile_number && (
                <p className="error-text">{errors.mobile_number.message}</p>
              )}
              <p className="text-sm text-gray-500 mt-1">
                Enter your mobile number (e.g., 03001234567 or +923001234567)
              </p>
            </div>

            <div>
              <label htmlFor="business_activity" className="label">
                Business Activity <span className="text-red-500">*</span>
              </label>
              <select
                id="business_activity"
                className={`input-field ${errors.business_activity ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                {...register('business_activity', {
                  required: 'Business Activity is required'
                })}
              >
                <option value="">Select Business Activity</option>
                <option value="Manufacturer">Manufacturer</option>
                <option value="Importer">Importer</option>
                <option value="Distributor">Distributor</option>
                <option value="Wholesaler">Wholesaler</option>
                <option value="Exporter">Exporter</option>
                <option value="Retailer">Retailer</option>
                <option value="Service Provider">Service Provider</option>
                <option value="Other">Other</option>
              </select>
              {errors.business_activity && (
                <p className="error-text">{errors.business_activity.message}</p>
              )}
              <p className="text-sm text-gray-500 mt-1">
                Select your primary business activity as per FBR requirements
              </p>
            </div>

            <div>
              <label htmlFor="sector" className="label">
                Business Sector <span className="text-red-500">*</span>
              </label>
              <select
                id="sector"
                className={`input-field ${errors.sector ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                {...register('sector', {
                  required: 'Business Sector is required'
                })}
              >
                <option value="">Select Business Sector</option>
                <option value="All Other Sectors">All Other Sectors</option>
                <option value="Steel">Steel</option>
                <option value="FMCG">FMCG</option>
                <option value="Textile">Textile</option>
                <option value="Telecom">Telecom</option>
                <option value="Petroleum">Petroleum</option>
                <option value="Electricity Distribution">Electricity Distribution</option>
                <option value="Gas Distribution">Gas Distribution</option>
                <option value="Services">Services</option>
                <option value="Automobile">Automobile</option>
                <option value="CNG Stations">CNG Stations</option>
                <option value="Pharmaceuticals">Pharmaceuticals</option>
                <option value="Wholesale / Retails">Wholesale / Retails</option>
                <option value="Potassium Chlorate">Potassium Chlorate</option>
                <option value="Cement or Concrete Blocks">Cement or Concrete Blocks</option>
                <option value="Mobile">Mobile</option>
              </select>
              {errors.sector && (
                <p className="error-text">{errors.sector.message}</p>
              )}
              <p className="text-sm text-gray-500 mt-1">
                Select your business sector as per FBR classification
              </p>
            </div>

            <div>
              <label htmlFor="email" className="label">
                Email address
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                className={`input-field ${errors.email ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                placeholder="Enter your email"
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address'
                  }
                })}
              />
              {errors.email && (
                <p className="error-text">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="label">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  className={`input-field pr-10 ${errors.password ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                  placeholder="Create a password"
                  {...register('password', {
                    required: 'Password is required',
                    minLength: {
                      value: 8,
                      message: 'Password must be at least 8 characters'
                    },
                    pattern: {
                      value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
                      message: 'Password must contain uppercase, lowercase, number, and special character'
                    }
                  })}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="error-text">{errors.password.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="label">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  className={`input-field pr-10 ${errors.confirmPassword ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                  placeholder="Confirm your password"
                  {...register('confirmPassword', {
                    required: 'Please confirm your password',
                    validate: value => value === password || 'Passwords do not match'
                  })}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="error-text">{errors.confirmPassword.message}</p>
              )}
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating account...
                    </div>
                  ) : (
                    'Create account'
                  )}
                </button>
              </div>

              <div className="text-center">
                <p className="text-sm text-gray-600">
                  Already have an account?{' '}
                  <Link
                    to="/login"
                    className="font-medium text-blue-600 hover:text-blue-500"
                  >
                    Sign in here
                  </Link>
                </p>
              </div>
            </form>

            <div className="mt-8 border-t border-gray-200 pt-6">
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Join FBRSTAPP Today
                </h3>
                <p className="text-sm text-gray-600">
                  Start submitting FBR-compliant invoices in minutes with our 
                  cloud-based platform.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
